import { Autocomplete } from '@mui/material';
import { FC, SetStateAction, useCallback, useMemo } from 'react';

import { SearchableSelect, SearchableSelectOption, StyledTextField } from 'components';
import { useLookups, useSizeTypes } from 'contexts';
import { Booking, Chassis, Container, Customer, EquipmentType, Genset, UnitStatus } from 'types';

import { InboundUnitSearch, OutboundUnitSearch } from '.';
import { useNewTransaction } from '..';
import { AccountSearch } from '../account';
import { BookingInput } from '../booking';
import { RedeliveryNumber } from '../redelivery';
import { EquipmentProps } from './equipment';
import ManufacturedDate from './manufactured-date';

const GensetColumn: FC<EquipmentProps> = ({ chassisRef, containerRef, gensetRef, required }) => {
  const { genset, chassis, container, transaction, setChassis, setContainer, setGenset } = useNewTransaction();
  const { unitStatusOptions } = useLookups();
  const { gensetSizeTypeEntries } = useSizeTypes();

  const primaryBooking: Partial<Booking> | undefined | null = useMemo(() => container.booking, [container.booking]);

  const sizeTypeOptions: SearchableSelectOption[] = useMemo(() => {
    return gensetSizeTypeEntries.filter(e => e[1].length === 1).map(e => ({ label: e[0], value: e[1][0] }));
  }, [gensetSizeTypeEntries]);

  /* memoize booking change handler to avoid booking input rerender */
  const setBooking = useCallback(
    (args?: SetStateAction<Partial<Booking> | undefined | null>) => {
      setGenset(g => new Genset({ ...g, booking: typeof args === 'function' ? args(g.booking) : args }));
    },
    [setGenset]
  );

  const setCustomer = useCallback(
    (customer?: Customer | null) => {
      setGenset(p => new Genset({ ...p, customer }));
    },
    [setGenset]
  );

  const setUnit = useCallback(
    (args?: SetStateAction<Genset>) => {
      setGenset(g => new Genset(typeof args === 'function' ? args(g) : args));
    },
    [setGenset]
  );

  const validateOutboundGenset = useCallback(
    async (unit: Genset | null) => {
      if (!unit) {
        return;
      }
      switch (unit.baseUnit?.equipmentType) {
        case EquipmentType.CHASSIS:
          if (!chassis.id) {
            setChassis(new Chassis(unit.baseUnit));
          } else if (chassis.id !== unit.baseUnit.id) {
            return `${unit.unitNumber} already mounted on ${unit.baseUnit.unitNumber} (${unit.baseUnit.equipmentType})`;
          } else if (!chassisRef.current?.isValid()) {
            chassisRef.current?.revalidate();
          }
          break;
        case EquipmentType.CONTAINER:
          if (!container.id) {
            setContainer(new Container(unit.baseUnit));
          } else if (container.id !== unit.baseUnit.id) {
            return `${unit.unitNumber} already mounted on ${unit.baseUnit.unitNumber} (${unit.baseUnit.equipmentType})`;
          } else if (!containerRef.current?.isValid()) {
            containerRef.current?.revalidate();
          }
          break;
      }
    },
    [chassis.id, chassisRef, container.id, containerRef, setChassis, setContainer]
  );

  return (
    <div className="d-flex flex-column g-inherit">
      <p className="text-primary-dark font-weight-bold">GENSET</p>
      <div>
        {transaction.inbound ? (
          <InboundUnitSearch
            key="genset"
            textFieldRef={gensetRef}
            required={required}
            setUnit={setUnit}
            unit={genset}
            equipmentType={EquipmentType.GENSET}
            data-cy="genset-inbound-unit-number-search"
          />
        ) : (
          <OutboundUnitSearch
            textFieldRef={gensetRef}
            required={required}
            unit={genset}
            setUnit={setUnit}
            customValidation={validateOutboundGenset}
            data-cy="genset-outbound-unit-number-search"
          />
        )}
      </div>
      <div>
        <SearchableSelect
          containerClass="text-field"
          inputClass={!genset.sizeType ? 'blank' : ''}
          id="genset-size-type"
          label="SIZE TYPE"
          disabled={!transaction.inbound}
          required={!gensetRef.current?.isBlank()}
          onClear={() => setUnit(p => ({ ...p, sizeType: undefined }))}
          onValueSelected={(sizeType: any) => setUnit(p => p && { ...p, sizeType })}
          options={sizeTypeOptions}
          value={genset.sizeType ?? ''}
          data-cy="genset-size-type-input"
        />
      </div>
      <div>
        <AccountSearch
          required={!gensetRef.current?.isBlank()}
          unit={genset}
          setCustomer={setCustomer}
          data-cy="genset-account-search"
        />
      </div>
      <div>
        <Autocomplete<UnitStatus | ''>
          fullWidth
          autoHighlight
          size="small"
          disabled={!transaction.inbound}
          options={['', ...unitStatusOptions]}
          value={genset.unitStatus ?? ''}
          onChange={(_, status) => setGenset(g => new Genset({ ...g, unitStatus: status || undefined }))}
          getOptionLabel={status => status ?? ''}
          renderInput={params => (
            <StyledTextField
              {...params}
              required={!gensetRef.current?.isBlank()}
              label="STATUS"
              data-cy="genset-unit-status-input"
            />
          )}
          renderOption={(props, status) => (
            <li {...props} key={status ?? ''}>
              {status ?? ''}
            </li>
          )}
        />
      </div>
      <BookingInput
        unit={genset}
        setBooking={setBooking}
        required={!primaryBooking && !transaction.inbound && !gensetRef.current?.isBlank()}
        data-cy="genset-booking-search"
      />
      {!!transaction.inbound && (
        <RedeliveryNumber data-cy="genset-redelivery-number-input" setUnit={setUnit} unit={genset} />
      )}
      <ManufacturedDate unit={genset} setUnit={setUnit} />
      <div className="checkboxes d-flex g-2 justify-content-center">
        <label className="font-weight-bold">CARB COMPLIANT</label>
        <input
          type="checkbox"
          checked={!!genset.carbCompliant}
          disabled={!transaction.inbound}
          onChange={e => setGenset(g => new Genset({ ...g, carbCompliant: e.target.checked }))}
          data-cy="genset-carb-compliant-checkbox"
        />
      </div>
    </div>
  );
};

export default GensetColumn;
